import React, { useState } from 'react';
import { X, Phone, MessageCircle } from 'lucide-react';

const galleryImages = [
  { id: 1, title: 'Coupler', code: 'ARK-IMG-01', url: 'https://customer-assets.emergentagent.com/job_construction-hire/artifacts/dw68psod_0fcc03aa-d1fd-41de-96b2-ee81dae4b867.jpeg' },
  { id: 2, title: 'C Channel', code: 'ARK-IMG-02', url: 'https://customer-assets.emergentagent.com/job_construction-hire/artifacts/yfakpx4x_c4a0667a-fe01-46a3-b85b-23a8d746a055.jpeg' },
  { id: 3, title: 'Props Scaffolding', code: 'ARK-IMG-03', url: 'https://customer-assets.emergentagent.com/job_construction-hire/artifacts/uzubmcie_eaf911f4-be08-494a-9ca8-d8158009eb27.jpeg' },
  { id: 4, title: 'Span', code: 'ARK-IMG-04', url: 'https://customer-assets.emergentagent.com/job_construction-hire/artifacts/n9fw51ab_265bce71-5882-46e9-afc9-d0f595e158a0.jpeg' },
  { id: 5, title: 'U Head', code: 'ARK-IMG-05', url: 'https://customer-assets.emergentagent.com/job_construction-hire/artifacts/k2rssh66_49ba7d91-6e55-45cd-a5cf-ebf2bfedf005.jpeg' },
  { id: 6, title: 'Base Jack', code: 'ARK-IMG-06', url: 'https://customer-assets.emergentagent.com/job_construction-hire/artifacts/p9v5o994_IMG_4938.jpeg' },
  { id: 7, title: 'Cuplock System', code: 'ARK-IMG-07', url: 'https://customer-assets.emergentagent.com/job_construction-hire/artifacts/l1gs4o03_6aa04f58-95f3-4283-a027-9c4170ecc676.jpeg' },
  { id: 8, title: 'H Frame Scaffolding', code: 'ARK-IMG-08', url: 'https://customer-assets.emergentagent.com/job_construction-hire/artifacts/1v6xyhhz_a1c1e135-5540-4d10-b4ed-bae978586933.jpeg' },
  { id: 9, title: 'Cuplock Diagram', code: 'ARK-IMG-09', url: 'https://customer-assets.emergentagent.com/job_construction-hire/artifacts/o9axj8wg_6aa04f58-95f3-4283-a027-9c4170ecc676.jpeg' },
];

const Gallery = () => {
  const [selected, setSelected] = useState(null);

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="gallery-page">
      {/* Hero (teal) */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-[#008080] via-[#0a7878] to-[#005a5a] text-white">
        <div className="absolute inset-0 bp-grid-on-dark opacity-50"></div>
        <div className="spotlight-gold bottom-0 right-0"></div>
        <span className="serial-ghost serial-ghost-light absolute right-4 top-12 text-[12rem] hidden md:block">YARD</span>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="ark-overline ark-overline-light">// archive · ark-yard</span>
          <h1 className="font-display font-black uppercase tracking-tighter text-white mt-5 text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.88]">
            Product<br /><span className="text-[#DAA520]">Gallery.</span>
          </h1>
          <p className="text-white/85 mt-6 max-w-2xl text-base md:text-lg">
            Browse a working selection of our scaffolding equipment — click to inspect each piece.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="relative py-20 md:py-24 bg-gray-50">
        <div className="absolute inset-0 bp-grid-fine opacity-40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {galleryImages.map((img) => (
              <button
                key={img.id}
                onClick={() => setSelected(img)}
                className="ark-card-beam group relative bg-white text-left flex flex-col"
                data-testid={`gallery-img-${img.id}`}
              >
                <div className="relative aspect-[4/3] bg-white overflow-hidden">
                  <div className="absolute top-4 right-4 z-20 font-mono text-[10px] tracking-[0.2em] text-gray-500 bg-white/95 px-2 py-1 uppercase border border-gray-200 rounded">
                    {img.code}
                  </div>
                  <img
                    src={img.url}
                    alt={img.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-5 border-t border-gray-200 flex items-center justify-between">
                  <h3 className="font-display font-bold text-[#008080] text-lg">{img.title}</h3>
                  <span className="font-mono text-[10px] tracking-widest text-[#DAA520] uppercase">// View</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setSelected(null); }}
            className="absolute top-4 right-4 w-11 h-11 border border-white/30 text-white hover:bg-white/10 flex items-center justify-center rounded"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={selected.url} alt={selected.title} className="w-full h-auto max-h-[80vh] object-contain bg-white rounded" />
            <div className="mt-5 flex items-center justify-between">
              <h3 className="font-display font-bold text-white text-2xl">{selected.title}</h3>
              <span className="font-mono text-xs tracking-[0.25em] text-[#DAA520] uppercase">// {selected.code}</span>
            </div>
          </div>
        </div>
      )}

      {/* CTA (teal) */}
      <section className="relative py-20 md:py-24 overflow-hidden bg-gradient-to-br from-[#008080] to-[#005a5a] text-white">
        <div className="absolute inset-0 bp-grid-on-dark opacity-40"></div>
        <div className="spotlight-gold left-1/2 -translate-x-1/2 -top-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.9]">
            Need more <span className="text-[#DAA520]">information?</span>
          </h2>
          <p className="text-white/85 mt-5">Contact us to discuss your scaffolding requirements.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+919766601155" className="ark-btn-primary"><Phone size={16} /> Call +91 9766601155</a>
            <a href="https://wa.me/919766601155" target="_blank" rel="noopener noreferrer" className="ark-btn-secondary ark-btn-secondary-dark"><MessageCircle size={16} /> WhatsApp Us</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
